import {
  Environment,
  Environments,
  getEnvironment,
} from "../entities/environment";
import featureJson from "../feature-toggles.json";
import {
  DEFAULT_ENVIRONMENT,
  FEATURE_AB_TESTING,
  FEATURE_NEW_RELIC,
  FEATURE_TEST_TOGGLE,
  FEATURE_PORSCHE_AT_JEWELS_POC,
} from "../utility/constants";
import { getConfig, isString } from "../utility/helper";
import { NaviError, NaviErrorTypes } from "../utility/navi-error";
import logger from "./logger";
import { navStateStore } from "../state/nav-state-store";
import { featureToggleStateStore } from "../state/feature-toggles-state";

interface FeatureMap {
  [featureName: string]: { enabled: boolean };
}

export class FeatureToggles {
  features: FeatureMap = {};

  env: Environment = "local";

  constructor(
    env = getEnvironment(DEFAULT_ENVIRONMENT),
    private readonly featureEnvMap: { [env: string]: FeatureMap } = featureJson,
  ) {
    this.env = env;
    this.features = this.featureEnvMap[env];
    featureToggleStateStore.state.features = this.featureEnvMap[env];
  }

  private async loadRemoteToggles(): Promise<void> {
    const configs = getConfig(this.env);
    const { locale } = navStateStore.state;
    let BFFEndpoint = configs.BFF_ENDPOINT;
    try {
      if (locale === "en-CN" || locale === "zh-CN") {
        BFFEndpoint = configs.BFF_CN_ENDPOINT;
      }

      const response = await fetch(
        `${BFFEndpoint}/toggles/header?env=${this.env}`,
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text);
      }

      const result = await response.json();

      this.features = result;
    } catch (err) {
      throw new NaviError(
        // eslint-disable-next-line
        `Load Feature Toggles failed with error: ${err} - ${BFFEndpoint}/toggles/header?env=${this.env}`,
        NaviErrorTypes.GENERAL,
      );
    }
  }

  private stringToBooleanStrict(value: string): boolean | null {
    if (value === "true") {
      return true;
    }
    if (value === "false") {
      return false;
    }
    return null;
  }

  private getUrlFeatureToggleOverride(
    feature: string,
    searchString: string,
  ): boolean | null {
    const queryValue = searchString
      .split("?")[1]
      ?.split("&")
      .find((query) => query.includes(feature))
      ?.split("=")[1];

    return isString(queryValue)
      ? this.stringToBooleanStrict(queryValue || "")
      : null;
  }

  isFeatureEnabled(feature: string, searchString: string): boolean {
    this.features = featureToggleStateStore.state.features;
    if (this.features === undefined) {
      return false;
    }

    if (
      this.env === Environments.PRODUCTION &&
      this.isFeatureNotReadyForProd(feature)
    ) {
      return false;
    }

    const featureToggleOverrides = [
      this.getUrlFeatureToggleOverride(feature, searchString),
    ];

    // eslint-disable-next-line
    for (const featureToggleOverride of featureToggleOverrides) {
      if (featureToggleOverride !== null) {
        return featureToggleOverride;
      }
    }

    if (this.features[feature] !== undefined) {
      return this.features[feature].enabled;
    }
    return false;
  }

  setEnvironment(env: Environment): void {
    this.env = getEnvironment(env);
    this.features = this.featureEnvMap[env];
  }

  async setFeatures(env: Environment): Promise<void> {
    this.env = getEnvironment(env);

    try {
      if (env === "test") {
        this.features = this.featureEnvMap[env];
        return;
      }
      await this.loadRemoteToggles();
    } catch (error) {
      logger.error("INIT_APP_CONFIG", error as Error);
      this.features = this.featureEnvMap[env];
      featureToggleStateStore.state.features = this.featureEnvMap[env];
    }
  }

  isFeatureNotReadyForProd(feature: string): boolean {
    const prodDisabledToggles: string | string[] = [];
    return prodDisabledToggles.includes(feature);
  }

  /* istanbul ignore next */
  isABTestingToggleActive = (): boolean =>
    this.isFeatureEnabled(FEATURE_AB_TESTING, window.location?.search ?? "");

  /* istanbul ignore next */
  isNewRelicEnabled = (): boolean =>
    this.isFeatureEnabled(FEATURE_NEW_RELIC, window.location?.search ?? "");

  /* istanbul ignore next */
  isTestToggleEnabled = (): boolean =>
    this.isFeatureEnabled(FEATURE_TEST_TOGGLE, window.location?.search ?? "");

  hasPorscheAtJewelsPOC = (): boolean =>
    this.isFeatureEnabled(
      FEATURE_PORSCHE_AT_JEWELS_POC,
      window.location?.search ?? "",
    );
}

export default new FeatureToggles();
